// Binding Rings (skill 'rings', owner 2026-11-02 'Make them all': restraint). Render-only: reads sim.ringHolds (which foe,
// where, held for how long) and the 'rings' / 'ringBreak' events; decides nothing. Violet seal look:
//  - cast: crisp violet lightning tethers from the hero's hand to every foe being bound
//  - each ring: two crossed energy bands (a level band at the waist and a tilted one turning around it) that SNAP shut from
//    wide to tight with a flash, then hold with a slow turn; runic dash segments run along them; a small seal circle glows on
//    the ground under the foe. In the last moments the bands flicker and their dashes split apart: the shatter is coming
//  - shatter: violet crystal shards, a white pinch star, a thin shock ring and short radial bolts; Shackle Nova adds a wider
//    blast ring. A ring whose foe died first just fades out
import * as THREE from 'three/webgpu';
import { abs, atan, dot, float, fract, length, mix, normalView, pow, positionViewDirection, smoothstep, step, time, uv, vec2, vec3 } from 'three/tsl';
import { instancedBufferAttribute } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import type { Fx } from './fx';
import { FLASH } from './fx';
import type { GroundGlow } from './groundGlow';
import type { Lightning } from './lightning';
import { Decals, clamp01, easeOut } from './skillsBShared';

const BODY = 0x9a4cff, RIM = 0x3a128e, CORE = 0xf6eeff, ZAP = 0x8a48ff, SHARD = 0xc8a8ff, GLOW = 0x9a60ff;
const POOL = 96;
const SNAP = 0.14, WARN = 0.3; // snap-shut time; flicker before the shatter (s)

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _e = new THREE.Euler(), _c = new THREE.Color();

/** energy band material: white-hot centre line, violet body, dark rim at the silhouette; runic dashes along the band whose
 *  gaps open as `crack` rises. a = (alpha, crack, flash, spin direction) */
function bandLook(a: any): THREE.MeshBasicNodeMaterial { // eslint-disable-line @typescript-eslint/no-explicit-any
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
  const facing = abs(dot(normalView, positionViewDirection)), hot = pow(facing, float(7));
  const along = fract(uv().x.mul(14).add(time.mul(0.8).mul(a.w)));
  const gap = mix(float(0.04), float(0.4), a.y);
  const dash = step(gap, along).mul(step(along, float(1).sub(gap.mul(0.3))));
  _c.setHex(BODY); const body = vec3(_c.r, _c.g, _c.b).mul(1.25);
  _c.setHex(RIM); const rim = vec3(_c.r, _c.g, _c.b);
  _c.setHex(CORE);
  const base = mix(rim, body, smoothstep(0.08, 0.45, facing));
  m.colorNode = mix(base, vec3(_c.r, _c.g, _c.b).mul(1.7), hot.mul(0.95).add(a.z).min(1));
  m.opacityNode = mix(float(0.35), float(1), dash).mul(a.x);
  return m;
}
/** small seal circle under a bound foe: a ring line with three notches. a = (alpha, crack, -, -) */
function sealLook(a: any): THREE.MeshBasicNodeMaterial { // eslint-disable-line @typescript-eslint/no-explicit-any
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const p = uv().sub(vec2(0.5, 0.5)), d = length(p).mul(2), ang = atan(p.y, p.x).div(Math.PI * 2);
  const line = smoothstep(0.05, 0.0, abs(d.sub(0.88)));
  const notch = step(0.8, fract(ang.mul(3).add(time.mul(0.3)))).mul(smoothstep(0.1, 0.0, abs(d.sub(0.72))));
  const fill = smoothstep(0.88, 0.0, d).mul(0.18);
  _c.setHex(BODY);
  m.colorNode = mix(vec3(_c.r, _c.g, _c.b).mul(1.2), vec3(1.4, 1.3, 1.6), line.mul(0.5));
  m.opacityNode = line.add(notch.mul(0.8)).add(fill).min(1).mul(a.x).mul(float(1).sub(a.y.mul(0.4)));
  return m;
}

class Bands {
  readonly mesh: THREE.InstancedMesh;
  private attr: THREE.InstancedBufferAttribute; n = 0;
  constructor(scene: THREE.Scene, tube: number) {
    this.attr = new THREE.InstancedBufferAttribute(new Float32Array(POOL * 4), 4); this.attr.setUsage(THREE.DynamicDrawUsage);
    this.mesh = new THREE.InstancedMesh(new THREE.TorusGeometry(1, tube, 6, 72).rotateX(Math.PI / 2), bandLook(instancedBufferAttribute(this.attr)), POOL);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 5;
    scene.add(this.mesh);
  }
  push(m: THREE.Matrix4, alpha: number, crack: number, flash: number, dir: number): void {
    if (this.n >= POOL) return;
    const i = this.n++; this.mesh.setMatrixAt(i, m);
    const A = this.attr.array as Float32Array; A[i * 4] = alpha; A[i * 4 + 1] = crack; A[i * 4 + 2] = flash; A[i * 4 + 3] = dir;
  }
  end(): void { this.mesh.count = this.n; if (this.n) { this.mesh.instanceMatrix.needsUpdate = true; this.attr.needsUpdate = true; } }
}

export class RingsFx {
  private level: Bands; private tilt: Bands; private seals: Decals;
  /** QA counters */
  casts = 0; bound = 0; shattered = 0;
  /** hero gesture: bumps per cast, with its yaw */
  castSeq = 0; castYaw = 0;
  private fade: { on: boolean; x: number; y: number; z: number; R: number; t: number }[] = Array.from({ length: 24 }, () => ({ on: false, x: 0, y: 0, z: 0, R: 1, t: 0 }));

  constructor(scene: THREE.Scene, private fx: Fx, private bolts: Lightning, private glow: GroundGlow, private groundAt: (x: number, z: number) => number) {
    this.level = new Bands(scene, 0.085);
    this.tilt = new Bands(scene, 0.065);
    this.seals = new Decals(scene, POOL, sealLook, 1);
  }

  /** waist height of a bound foe of body radius s standing at (x, z), plus any Gravity Grip lift */
  private bodyY(x: number, z: number, s: number, lift: number): number { return this.groundAt(x, z) + 0.5 + Math.min(1.6, s * 0.75) + lift; }
  private R(s: number): number { return s * 1.2 + 0.38; }
  private liftOf(sim: Sim, ei: number, eid: number): number { const e = sim.enemies[ei]; return e && e.alive && e.id === eid ? e.gripY : 0; }

  event(ev: SimEvent, sim: Sim): void {
    const fx = this.fx;
    if (ev.t === 'rings') {
      this.casts++; this.castSeq++;
      const p = sim.player; this.castYaw = Math.atan2(ev.x - p.x, ev.z - p.z);
      const hx = p.x + Math.sin(this.castYaw) * 0.6, hz = p.z + Math.cos(this.castYaw) * 0.6, hy = this.groundAt(p.x, p.z) + 1.35;
      fx.stars.add(hx, hy, hz, 1.2, 0.12, CORE, 2.4);
      for (const r of sim.ringHolds) {
        if (!r.alive || r.t > 0 || r.dmg <= 0) continue;
        this.bound++;
        const y = this.bodyY(r.x, r.z, r.s, this.liftOf(sim, r.ei, r.eid));
        this.bolts.add([hx, hy, hz, (hx + r.x) / 2, Math.max(hy, y) + 0.6, (hz + r.z) / 2, r.x, y, r.z], ZAP, 0.18, 0.16, 0.45);
        fx.stars.add(r.x, y, r.z, this.R(r.s) * 1.6, 0.12, 0xe8dcff, 2.2);
      }
      this.glow.flash(ev.x, ev.z, ev.r, GLOW, 0.45, 0.25);
    } else if (ev.t === 'ringBreak') {
      const gy = this.groundAt(ev.x, ev.z), y = this.bodyY(ev.x, ev.z, ev.s, Math.max(0, ev.y - (ev.s > 2 ? 2.6 : 1.6))), R = this.R(ev.s);
      if (!ev.burst) { const f = this.fade.find((q) => !q.on); if (f) Object.assign(f, { on: true, x: ev.x, y, z: ev.z, R, t: 0 }); fx.particles.emit(ev.x, y, ev.z, 6, 2.5, SHARD, 0.18, 0.4, 0.5, 0, 3, 2); return; }
      this.shattered++; { const f = this.fade.find((q) => !q.on); if (f) Object.assign(f, { on: true, x: ev.x, y, z: ev.z, R, t: 0 }); } // the band itself bursts outward
      fx.shatter(ev.x, y, ev.z, ev.evolved ? 12 : 9, 7, SHARD, 0.42);
      fx.stars.add(ev.x, y, ev.z, R * 1.7, 0.12, 0xffffff, 2.6);
      fx.stars.add(ev.x, y, ev.z, R * 2.4, 0.2, BODY, 1.5);
      fx.rings.add(ev.x, gy + 0.12, ev.z, R * 2.1, 0.3, BODY, 2.2, 0.6);
      for (let k = 0; k < 4; k++) { const a = (k / 4) * Math.PI * 2 + Math.random() * 0.8, L = R * (1.4 + Math.random() * 0.6); this.bolts.add([ev.x, y, ev.z, ev.x + Math.cos(a) * L * 0.5, y + (Math.random() - 0.3) * 0.6, ev.z + Math.sin(a) * L * 0.5, ev.x + Math.cos(a) * L, y - 0.3, ev.z + Math.sin(a) * L], ZAP, 0.16, 0.14, 0.35); }
      if (ev.blastR > 0) { // Shackle Nova: the shatter blasts outward
        fx.rings.add(ev.x, gy + 0.14, ev.z, ev.blastR * 1.15, 0.4, 0xc090ff, 2.4, 0.8);
        fx.flashes.add(ev.x, y, ev.z, ev.blastR * 0.45, 0.16, BODY, 1.0);
        for (let k = 0; k < 10; k++) { const a = Math.random() * Math.PI * 2, sp = ev.blastR * 3; fx.particles.emitDir(ev.x, y, ev.z, Math.cos(a) * sp, 1 + Math.random() * 2, Math.sin(a) * sp, k % 2 ? BODY : CORE, 0.24, 0.3, 3); }
        this.glow.flash(ev.x, ev.z, ev.blastR * 1.2, GLOW, 0.7, 0.3);
      } else this.glow.flash(ev.x, ev.z, R * 1.6, GLOW, 0.5, 0.2);
      if (FLASH.dim >= 1 && this.shattered % 3 === 1) fx.lights.flash(ev.x, y, ev.z, 0xa070ff, 14, 0.2, 8);
      fx.shake = Math.min(1, fx.shake + 0.05);
    }
  }

  update(sim: Sim, dt: number): void {
    this.level.n = 0; this.tilt.n = 0; this.seals.begin();
    const tm = performance.now() / 1000;
    for (const r of sim.ringHolds) {
      if (!r.alive) continue;
      const gy = this.groundAt(r.x, r.z), y = this.bodyY(r.x, r.z, r.s, this.liftOf(sim, r.ei, r.eid)), R0 = this.R(r.s);
      const snap = clamp01(r.t / SNAP), close = 1.9 - 0.9 * easeOut(snap), wob = snap < 1 ? 0 : Math.sin(tm * 3 + r.id) * 0.04;
      const left = r.dur - r.t, crack = clamp01(1 - left / WARN), flick = crack > 0 ? 0.6 + 0.4 * Math.sin(tm * 60 + r.id) : 1;
      const flash = snap < 1 ? 0.55 * (1 - snap) : crack * 0.5 * (0.5 + 0.5 * Math.sin(tm * 45));
      const small = r.dmg <= 0 ? 0.75 : 1, R = R0 * close * small, alpha = (snap < 1 ? 0.6 + 0.4 * snap : 1) * flick;
      _q.setFromEuler(_e.set(wob, tm * 0.8 + r.id, 0)); _s.set(R, R * (1 + crack * 0.1), R);
      this.level.push(_m.compose(_p.set(r.x, y, r.z), _q, _s), alpha, crack, flash, 1);
      _q.setFromEuler(_e.set(1.1, -tm * (snap < 1 ? 9 : 1.6) - r.id * 1.7, 0.25, 'YXZ')); _s.setScalar(R * 0.92);
      this.tilt.push(_m.compose(_p.set(r.x, y + 0.05, r.z), _q, _s), alpha * 0.95, crack, flash, -1);
      this.seals.push(r.x, gy + 0.06, r.z, R0 * 2.3 * small, tm * 0.4 + r.id, clamp01(r.t / 0.2) * 0.85, crack);
      if (Math.random() < 0.06) this.fx.particles.emitDir(r.x + (Math.random() - 0.5) * R, y, r.z + (Math.random() - 0.5) * R, 0, 0.8, 0, BODY, 0.12, 0.45, 2.4);
    }
    // rings whose foe died: a quick fade outward
    for (const f of this.fade) {
      if (!f.on) continue;
      f.t += dt; const u = clamp01(f.t / 0.2); if (u >= 1) { f.on = false; continue; }
      _q.identity(); _s.setScalar(f.R * (1 + u * 0.9));
      this.level.push(_m.compose(_p.set(f.x, f.y, f.z), _q, _s), 1 - u, 1, 0, 1);
    }
    this.level.end(); this.tilt.end(); this.seals.end();
  }

  glowPools(g: GroundGlow, sim: Sim): void {
    let n = 0;
    for (const r of sim.ringHolds) if (r.alive && n++ < 40) g.blob(r.x, this.groundAt(r.x, r.z) + 1, r.z, this.R(r.s) * 1.3, GLOW, 0.55);
  }

  clear(): void { this.level.n = 0; this.tilt.n = 0; this.level.end(); this.tilt.end(); this.seals.clear(); for (const f of this.fade) f.on = false; }
}
